import React, { useMemo } from 'react'
import { useFormik } from 'formik'
import { Button, Grid, CircularProgress, FormControlLabel, Switch } from '@mui/material'
import { Input } from '@fullscreendigital/carrefour-dex-fe'
import { Modal } from 'components/core-ui/Modal'
import { useNotification } from 'hooks'
import { useCreateDelegateMutation, useDelegateQuery, useUpdateDelegateMutation } from '../../api'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { EditDelegateModalProps } from '../../interfaces'
import { delegatesValidationSchema } from './helpers'

const EditDelegateModal: React.FC<EditDelegateModalProps> = ({
  isModalOpen = false,
  onModalClose,
  delegateId,
  onSuccess,
  selectDelegate
}) => {
  const { showNotification } = useNotification()

  const shouldRefreshData = useMemo(() => isModalOpen === true, [isModalOpen])

  const { data: delegate, isLoading } = useDelegateQuery(
    showNotification,
    delegateId,
    shouldRefreshData
  )

  const updateDelegateMutation = useUpdateDelegateMutation(showNotification, () => {
    formik.setFieldValue('isEnabled', delegate?.isEnabled ?? true)
  })
  const createDelegateMutation = useCreateDelegateMutation(showNotification)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: delegate?.id || undefined,
      firstName: delegate?.firstName || '',
      lastName: delegate?.lastName || '',
      email: delegate?.email || '',
      phone: delegate?.phone || '',
      managers: delegate?.managers || [],
      isEnabled: delegate?.isEnabled ?? true
    },
    validationSchema: delegatesValidationSchema,
    onSubmit: async values => {
      let response
      if (!delegateId) {
        values.managers = []
        response = await createDelegateMutation.mutateAsync(values)
      } else {
        values.id = delegateId
        response = await updateDelegateMutation.mutateAsync({
          managerId: values.managers?.[0]?.id,
          delegateId,
          updatedData: values
        })
      }
      selectDelegate && selectDelegate(response)
      onModalClose()
      onSuccess()
      formik.resetForm()
    }
  })

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={onModalClose}
      modalTitle={delegateId ? 'Edit Delegate' : 'Create Delegate'}
      modalWidth="50%"
    >
      <Grid container spacing={2}>
        {isLoading || updateDelegateMutation.isLoading ? (
          <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
        ) : (
          <>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="firstName"
                label="First Name"
                width="100%"
                disabled={!!delegateId}
                value={formik.values.firstName}
                required
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="lastName"
                label="Last Name"
                width="100%"
                disabled={!!delegateId}
                value={formik.values.lastName}
                required
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="email"
                label="Email"
                width="100%"
                value={formik.values.email}
                required
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="phone"
                label="Phone"
                width="100%"
                value={formik.values.phone}
                required
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
            </Grid>
            {delegateId && (
              <Grid item xs={12}>
                <span style={{ display: 'inline-block', lineHeight: '38px', marginRight: '12px' }}>
                  Status:
                </span>
                <FormControlLabel
                  control={
                    <Switch
                      name="isEnabled"
                      checked={formik.values.isEnabled}
                      onChange={event => formik.setFieldValue('isEnabled', event.target.checked)}
                      onBlur={formik.handleBlur}
                    />
                  }
                  label="Enabled"
                />
                <span
                  style={{
                    color: 'rgba(0, 0, 0, 0.38)',
                    display: 'inline-block',
                    lineHeight: '38px'
                  }}
                >
                  (can be disabled if no active assigned partners)
                </span>
              </Grid>
            )}
            <Grid item xs={12} className={s.modalButtons}>
              <Button variant="outlined" onClick={onModalClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => formik.handleSubmit()}
                disabled={formik.isSubmitting}
              >
                Save
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </Modal>
  )
}

export default EditDelegateModal
